/**
 * Wholesale application form — client module.
 *
 * UMD: loadable under Node for tests, and `window.QCCWholesaleApply` inside
 * the Shopify theme. Mirrors the accepted coffee-match.js pattern.
 *
 * CONTRACT: Rumi coffee-pl-bot 49f85ce5 (Sales Task 4 / N-3, accepted by
 * independent review 0d51b5af), wholesale/apply.py.
 *
 *   1. WIRE KEYS. The server refuses any key outside `_KNOWN_FIELDS` with a
 *      400, so this module sends only: the seven base fields (legal_name,
 *      contact_name, email, phone, business_type, location, notes),
 *      `interests`, `qcc_trap`, `cf-turnstile-response`, and
 *      `interest_other_text` only when `other` is chosen. The historical
 *      attribution and disclosure-stamp keys are retired: Rumi 49f85ce5
 *      would refuse every application that carried them. The source is fixed
 *      server-side and cannot be set from here.
 *   2. INTERESTS are exactly `APPLICATION_INTERESTS` (PM ruling P-1): eleven
 *      wire keys, sent as checked, in form order, with no alias or
 *      transformation. The section markup renders the same list.
 *   3. RESPONSES (brief section 8.5):
 *        200 {"status":"received"}         success view
 *        400 {"status":"invalid", fields, message}  message + named fields
 *        403 / 429 / 503 {status, message}  message
 *      The copy shown is always the server's `message`. The pre-49f85ce5
 *      server answered errors under a different key; this module never
 *      reads it. Every non-success resets the Turnstile widget, because a
 *      token is single-use.
 *   4. Client validation follows the server's rules for the required
 *      fields, email, phone and the other/other-text pairing, and never
 *      accepts a value Rumi refuses on those rules. It is STRICTER on
 *      business_type (only the six rendered values) and does not repeat the
 *      server's length, control-character or payment-card checks; those come
 *      back as a 400 with a message and are correctable.
 *
 * WRITE SAFETY. At 49f85ce5 a 400, 403, 429 or 503 carrying a `message` is
 * returned before intake or after a rolled-back intake, so it wrote nothing
 * and the form is released for a corrected or later retry, as the server's
 * own copy invites. Anything else - another status, a body without a
 * message, a redirect somewhere unexpected, a network failure - is
 * AMBIGUOUS: the row may exist, so the module shows support guidance and
 * keeps the button disabled.
 *
 * This module adds no analytics or tracking call and contacts no host other
 * than the Rumi apply endpoint (and the Turnstile widget the section loads).
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.QCCWholesaleApply = api;
    api.autoMount();
  }
}(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var APPLY_ENDPOINT = 'https://rumi.queencitycoffeeroasters.com/apply';

  /**
   * Canonical customer-facing wholesale support mailbox (D-013), the same
   * value as Rumi's `wholesale.ux_copy.SUPPORT_EMAIL` at 49f85ce5. Customer
   * route only: no internal alert channel may appear in customer copy.
   */
  var SUPPORT_CONTACT = 'wholesale@qcitycoffee.com';

  var OUTCOME = Object.freeze({
    OK: 'ok',
    REFUSED: 'refused',
    AMBIGUOUS: 'ambiguous'
  });

  /**
   * WHITESPACE PARITY. `_get_field` calls Python's `.strip()` and
   * `_validate_email` uses Python's `\s`. Neither is JavaScript's.
   *
   * Measured against the pinned python:3.11-slim runtime on 2026-09-04:
   * Python treats 29 codepoints as whitespace; JavaScript's `\s` and
   * `String.prototype.trim` disagree on six of them. Five are the dangerous
   * direction - U+001C, U+001D, U+001E, U+001F and U+0085 are whitespace to
   * Python but not to JavaScript, so `"a\u0085b@x.co"` passed the old client
   * regex and was rejected by the server with a 400. The sixth, U+FEFF, is
   * the safe direction. Both are eliminated below.
   *
   * WS_RANGES is that exact 29-codepoint set (10 ranges), and `.isspace()`
   * and regex `\s` were verified to be the same set in that runtime, so one
   * table serves both stripping and matching. WS_CLASS is the same set as a
   * regex character class, written as ASCII escapes - never raw control
   * characters, which would be invisible in a diff.
   *
   * sha256 of the canonical range list:
   *   8d0e0314a86200647ecaa737cfbff0c7adea256673c381342d36acd84844a455
   */
  var WS_RANGES = [[9,13],[28,32],[133,133],[160,160],[5760,5760],[8192,8202],[8232,8233],[8239,8239],[8287,8287],[12288,12288]];
  var WS_CLASS = '\u0009-\u000D\u001C-\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028-\u2029\u202F\u205F\u3000';

  function inWsRanges(cp) {
    var lo = 0, hi = WS_RANGES.length - 1;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1, r = WS_RANGES[mid];
      if (cp < r[0]) { hi = mid - 1; } else if (cp > r[1]) { lo = mid + 1; } else { return true; }
    }
    return false;
  }

  function cpAtIndex(s, i) {
    var c = s.charCodeAt(i);
    if (c >= 0xD800 && c <= 0xDBFF && i + 1 < s.length) {
      var d = s.charCodeAt(i + 1);
      if (d >= 0xDC00 && d <= 0xDFFF) { return [(c - 0xD800) * 0x400 + (d - 0xDC00) + 0x10000, 2]; }
    }
    return [c, 1];
  }

  /* Python's str.strip(), not JavaScript's trim(). Used for EVERY field, so
     a value of only U+001C is empty here exactly as it is on the server. */
  function trim(v) {
    var s = (v === null || v === undefined) ? '' : String(v), a = 0, b = s.length;
    while (a < b) { var r = cpAtIndex(s, a); if (!inWsRanges(r[0])) { break; } a += r[1]; }
    while (b > a) {
      var c = s.charCodeAt(b - 1), cp = c, step = 1;
      if (c >= 0xDC00 && c <= 0xDFFF && b - 2 >= a) {
        var h = s.charCodeAt(b - 2);
        if (h >= 0xD800 && h <= 0xDBFF) { cp = (h - 0xD800) * 0x400 + (c - 0xDC00) + 0x10000; step = 2; }
      }
      if (!inWsRanges(cp)) { break; }
      b -= step;
    }
    return s.slice(a, b);
  }

  /* apply.py::_validate_email, with Python's whitespace set substituted for
     JavaScript's. Built with new RegExp so the class stays readable ASCII. */
  var EMAIL_RE = new RegExp('^[^@' + WS_CLASS + ']+@[^@' + WS_CLASS + ']+\\.[^@' + WS_CLASS + ']+$');
  function isValidEmail(value) { return EMAIL_RE.test(trim(value)); }

  /**
   * apply.py::_validate_phone - decimal digits only, at least ten.
   *
   * PARITY WITH THE PINNED SERVER RUNTIME. Python removes everything outside
   * Unicode general category Nd. Which codepoints those are depends on the
   * interpreter's Unicode version, so the table below is generated against
   * the runtime the server actually pins, not against whatever Python or
   * browser happens to be nearest.
   *
   *   Server runtime  : python:3.11-slim
   *                     (Dockerfile FROM, nixpacks python311, railpack "3.11")
   *   Reproduced with : Python 3.11.15, unicodedata 14.0.0
   *   Result          : 660 codepoints in 62 ranges
   *   sha256 of the canonical range list:
   *     2298ea77ba3fbbd20b612e2e202821746f9191252835cfaf890be669f2ce06d2
   *   Verified codepoint-by-codepoint across all 1,114,112 codepoints on
   *   2026-09-04: exact match.
   *
   * WHY NOT THE ENGINE'S OWN CHARACTER CLASS, AND WHY NOT A NEWER PYTHON.
   * Both are looser than the server, and looser is the dangerous direction -
   * it lets the client accept a value the server rejects. Measured the same
   * day: Node v25.7.0 counts 770; Python 3.14 / Unicode 16.0.0 counts 760,
   * a full 100 codepoints the pinned server does not recognise, across
   * U+10D40..U+10D49, U+116D0..U+116E3, U+11BF0..U+11BF9, U+11F50..U+11F59,
   * U+16130..U+16139, U+16D70..U+16D79, U+1CCF0..U+1CCF9, U+1E4F0..U+1E4F9
   * and U+1E5F1..U+1E5FA. A table pinned to the server's own Unicode version
   * cannot drift that way, needs no ES2018 feature, and behaves identically
   * on every engine.
   *
   * REGENERATION. Only when the server's pinned Python changes. Regenerate in
   * THAT interpreter, never a newer one, and update the version fixture the
   * tests pin:
   *   python3.11 -c "import unicodedata as u; ..."
   * Do not hand-edit a range.
   *
   * SCOPE. This counts digits. It does not normalise a phone number, parse a
   * dialling plan, or say anything about whether the number is reachable or
   * deliverable. The server applies the same count and claims no more.
   */
  var ND_UNICODE_VERSION = '14.0.0';
  var ND_SOURCE_RUNTIME = 'python:3.11-slim';
  var ND_RANGES = [[48,57],[1632,1641],[1776,1785],[1984,1993],[2406,2415],[2534,2543],[2662,2671],[2790,2799],[2918,2927],[3046,3055],[3174,3183],[3302,3311],[3430,3439],[3558,3567],[3664,3673],[3792,3801],[3872,3881],[4160,4169],[4240,4249],[6112,6121],[6160,6169],[6470,6479],[6608,6617],[6784,6793],[6800,6809],[6992,7001],[7088,7097],[7232,7241],[7248,7257],[42528,42537],[43216,43225],[43264,43273],[43472,43481],[43504,43513],[43600,43609],[44016,44025],[65296,65305],[66720,66729],[68912,68921],[69734,69743],[69872,69881],[69942,69951],[70096,70105],[70384,70393],[70736,70745],[70864,70873],[71248,71257],[71360,71369],[71472,71481],[71904,71913],[72016,72025],[72784,72793],[73040,73049],[73120,73129],[92768,92777],[92864,92873],[93008,93017],[120782,120831],[123200,123209],[123632,123641],[125264,125273],[130032,130041]];

  function isDecimalDigitCodePoint(cp) {
    var lo = 0, hi = ND_RANGES.length - 1;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1, r = ND_RANGES[mid];
      if (cp < r[0]) { hi = mid - 1; } else if (cp > r[1]) { lo = mid + 1; } else { return true; }
    }
    return false;
  }

  /* Surrogate pairs are decoded by hand so this stays ES5-safe: no
     codePointAt, no property escapes, no regex feature detection. */
  function digitCount(value) {
    var s = trim(value), n = 0, i = 0;
    while (i < s.length) {
      var c = s.charCodeAt(i), cp = c, step = 1;
      if (c >= 0xD800 && c <= 0xDBFF && i + 1 < s.length) {
        var d = s.charCodeAt(i + 1);
        if (d >= 0xDC00 && d <= 0xDFFF) { cp = (c - 0xD800) * 0x400 + (d - 0xDC00) + 0x10000; step = 2; }
      }
      if (isDecimalDigitCodePoint(cp)) { n++; }
      i += step;
    }
    return n;
  }
  function isValidPhone(value) { return digitCount(value) >= 10; }

  /** The six selectable option values. The empty placeholder is not one. */
  var BUSINESS_TYPES = ['cafe', 'restaurant', 'hotel', 'office', 'retail', 'other'];

  /**
   * Rumi 49f85ce5 `APPLICATION_INTERESTS`, verbatim: [group, wire key, label]
   * in form order. The section renders the same eleven checkboxes, each with
   * id `applyInterest-<key>`. No alias exists; never add one here.
   */
  var INTERESTS = [
    ['Coffee and drinks', 'roasted_coffee', 'Roasted coffee'],
    ['Coffee and drinks', 'cold_brew', 'Cold brew'],
    ['Coffee and drinks', 'syrups', 'Syrups'],
    ['Coffee and drinks', 'pastries', 'Pastries'],
    ['Equipment', 'equipment_purchase', 'Buy equipment'],
    ['Equipment', 'equipment_leasing', 'Lease equipment'],
    ['Equipment', 'equipment_rental', 'Rent equipment'],
    ['Equipment', 'maintenance_service', 'Maintenance and service'],
    ['Help for your team', 'training', 'Education and training'],
    ['Help for your team', 'consulting', 'Consulting'],
    ['Something else', 'other', 'Something else (tell us)']
  ];
  var INTEREST_ID_PREFIX = 'applyInterest-';
  var OTHER_INTEREST = 'other';
  var OTHER_TEXT_KEY = 'interest_other_text';
  var OTHER_TEXT_MAX = 500;

  /**
   * Honeypot (brief section 7, m-8). Its name, id and surroundings avoid the
   * browser autofill vocabulary; the old name matched it and is retired.
   * Its value is sent untouched: a filled trap gets Rumi's ordinary accepted
   * response and writes nothing.
   */
  var HONEYPOT_FIELD = 'qcc_trap';
  var HONEYPOT_ID = 'applyQccTrap';

  /** The hidden input Cloudflare's widget writes inside the form. */
  var TOKEN_FIELD = 'cf-turnstile-response';
  var TURNSTILE_ID = 'applyTurnstile';

  /**
   * `purposeToken` marks the controls WCAG 1.3.5 (F-041) actually governs —
   * those collecting information about the user, named by its own token list.
   * autocomplete="off" is a valid attribute value but is NOT a purpose token.
   */
  var FIELDS = [
    { key: 'legal_name',    id: 'applyBusiness', label: 'Business name',       autocomplete: 'organization',   purposeToken: true,  required: true,  type: 'text',     errorId: 'applyBusinessError' },
    { key: 'contact_name',  id: 'applyContact',  label: 'Your name',           autocomplete: 'name',           purposeToken: true,  required: true,  type: 'text',     errorId: 'applyContactError'  },
    { key: 'phone',         id: 'applyPhone',    label: 'Phone number',        autocomplete: 'tel',            purposeToken: true,  required: true,  type: 'tel',      errorId: 'applyPhoneError'    },
    { key: 'email',         id: 'applyEmail',    label: 'Email address',       autocomplete: 'email',          purposeToken: true,  required: true,  type: 'email',    errorId: 'applyEmailError'    },
    { key: 'business_type', id: 'applyType',     label: 'Business type',       autocomplete: 'off',            purposeToken: false, required: true,  type: 'select',   errorId: 'applyTypeError'     },
    { key: 'location',      id: 'applyLocation', label: 'City or town',        autocomplete: 'address-level2', purposeToken: true,  required: false, type: 'text',     errorId: 'applyLocationError' },
    { key: 'notes',         id: 'applyMessage',  label: 'About your business', autocomplete: 'off',            purposeToken: false, required: false, type: 'textarea', errorId: 'applyMessageError'  }
  ];

  var INTERESTS_TARGET = { key: 'interests', id: null, focusId: INTEREST_ID_PREFIX + INTERESTS[0][1], label: 'What you’re interested in', errorId: 'applyInterestsError' };
  var OTHER_TEXT_TARGET = { key: OTHER_TEXT_KEY, id: 'applyInterestOther', wrapId: 'applyInterestOtherWrap', label: 'Something else (tell us)', errorId: 'applyInterestOtherError' };

  /** Every place an error can be painted, in document order. */
  var ERROR_TARGETS = FIELDS.slice(0, 5).concat([INTERESTS_TARGET, OTHER_TEXT_TARGET], FIELDS.slice(5));

  var MESSAGES = {
    legal_name:    'Enter your business name.',
    contact_name:  'Enter your name.',
    phone:         'Enter a phone number with at least 10 digits.',
    email:         'Enter a valid email address, for example name@business.com.',
    business_type: 'Choose the option that best describes your business.',
    interest_other_text: 'Tell us what else you’re looking for.',
    interest_other_text_long: 'Keep this to 500 characters or fewer.',
    server_field: 'Check this field.'
  };

  var CHECK_FIELDS_TEXT = 'Check the highlighted fields and try again.';
  var FALLBACK_400_TEXT = 'We could not submit that. Check your details and try again.';
  var AMBIGUOUS_TEXT =
    'We could not confirm your application went through, and we may have received it anyway. ' +
    'Please do not resubmit — contact ' + SUPPORT_CONTACT + ' and we will check.';

  function inClosedSet(list, v) {
    for (var i = 0; i < list.length; i++) { if (list[i] === v) { return true; } }
    return false;
  }

  /** The chosen wire keys, in form order, each at most once. Unknown values are dropped. */
  function selectedInterests(chosen) {
    var list = chosen || [], out = [];
    for (var i = 0; i < INTERESTS.length; i++) {
      if (inClosedSet(list, INTERESTS[i][1])) { out.push(INTERESTS[i][1]); }
    }
    return out;
  }

  /**
   * Refuses in the server's own order: required fields first, then format,
   * then the other/other-text pairing. A value this returns ok:true for
   * passes those Rumi rules.
   */
  function validate(values) {
    var errors = {};
    var v = values || {};

    for (var i = 0; i < FIELDS.length; i++) {
      var f = FIELDS[i];
      var raw = trim(v[f.key]);

      if (f.required && !raw) {
        errors[f.key] = MESSAGES[f.key];
      } else if (f.key === 'business_type' && raw && !inClosedSet(BUSINESS_TYPES, raw)) {
        errors[f.key] = MESSAGES.business_type;
      } else if (f.key === 'email' && raw && !isValidEmail(raw)) {
        errors[f.key] = MESSAGES.email;
      } else if (f.key === 'phone' && raw && !isValidPhone(raw)) {
        errors[f.key] = MESSAGES.phone;
      }
    }

    if (inClosedSet(selectedInterests(v.interests), OTHER_INTEREST)) {
      var other = trim(v[OTHER_TEXT_KEY]);
      if (!other) { errors[OTHER_TEXT_KEY] = MESSAGES.interest_other_text; }
      else if (other.length > OTHER_TEXT_MAX) { errors[OTHER_TEXT_KEY] = MESSAGES.interest_other_text_long; }
    }

    var firstInvalid = null;
    for (var j = 0; j < ERROR_TARGETS.length && firstInvalid === null; j++) {
      if (errors[ERROR_TARGETS[j].key]) { firstInvalid = ERROR_TARGETS[j].key; }
    }
    return { ok: firstInvalid === null, errors: errors, firstInvalid: firstInvalid };
  }

  function buildPayload(values) {
    var v = values || {};
    var interests = selectedInterests(v.interests);
    var payload = {
      legal_name: trim(v.legal_name),
      contact_name: trim(v.contact_name),
      email: trim(v.email),
      phone: trim(v.phone),
      business_type: trim(v.business_type),
      location: trim(v.location),
      notes: trim(v.notes),
      interests: interests
    };
    if (inClosedSet(interests, OTHER_INTEREST)) { payload[OTHER_TEXT_KEY] = trim(v[OTHER_TEXT_KEY]); }
    var trap = v[HONEYPOT_FIELD];
    payload[HONEYPOT_FIELD] = (trap === null || trap === undefined) ? '' : String(trap);
    payload[TOKEN_FIELD] = v[TOKEN_FIELD] ? String(v[TOKEN_FIELD]) : '';
    return payload;
  }

  /**
   * LEGACY - remove once Rumi 49f85ce5 or later is live. Before 49f85ce5 the
   * server answered a stored application with a redirect to this page, which
   * fetch follows. Accepting it lets this theme ship before or with Rumi.
   * The comparison is exact on the path, so a login wall or an interstitial
   * never counts as success.
   */
  var LEGACY_APPLY_SUCCESS_URL = 'https://rumi.queencitycoffeeroasters.com/apply/success';

  function canonicalSuccessUrl(url) {
    var u = trim(url);
    if (!u) { return ''; }
    u = u.split('#')[0].split('?')[0];
    var m = /^([a-z][a-z0-9+.-]*:\/\/[^\/]+)(\/.*)?$/i.exec(u);
    if (!m) { return ''; }
    var origin = m[1].toLowerCase();
    var path = m[2] || '/';
    if (path.length > 1 && path.charAt(path.length - 1) === '/') {
      path = path.slice(0, -1);
    }
    return origin + path;
  }

  function isLegacySuccessUrl(url) {
    var got = canonicalSuccessUrl(url);
    return got !== '' && got === canonicalSuccessUrl(LEGACY_APPLY_SUCCESS_URL);
  }

  /** Statuses whose body carries a customer `message` at 49f85ce5. */
  var MESSAGE_STATUSES = [400, 403, 429, 503];
  var CORRECTABLE_STATUS = 400;

  function readJson(res) {
    if (!res || typeof res.json !== 'function') { return Promise.resolve(null); }
    try {
      return Promise.resolve(res.json()).then(function (d) {
        return (d && typeof d === 'object') ? d : null;
      }, function () { return null; });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  /** The server's customer copy, and nothing else from the body. */
  function serverMessage(data) {
    if (!data) { return ''; }
    var message = data.message;
    return typeof message === 'string' ? trim(message) : '';
  }

  function serverFields(data) {
    var out = [];
    var list = data && data.fields;
    if (!list || typeof list.length !== 'number') { return out; }
    for (var i = 0; i < list.length && out.length < 20; i++) {
      if (typeof list[i] === 'string' && list[i] && !inClosedSet(out, list[i])) { out.push(list[i]); }
    }
    return out;
  }

  /** @returns Promise<{outcome, message, fields}> */
  function interpretResponse(res) {
    if (!res) { return Promise.resolve({ outcome: OUTCOME.AMBIGUOUS }); }
    var status = Number(res.status);
    return readJson(res).then(function (data) {
      if (res.ok) {
        if (data && data.status === 'received') { return { outcome: OUTCOME.OK }; }
        if (isLegacySuccessUrl(res.url)) { return { outcome: OUTCOME.OK }; }
        return { outcome: OUTCOME.AMBIGUOUS };
      }
      if (!inClosedSet(MESSAGE_STATUSES, status)) { return { outcome: OUTCOME.AMBIGUOUS }; }
      var message = serverMessage(data);
      var fields = status === CORRECTABLE_STATUS ? serverFields(data) : [];
      if (message) { return { outcome: OUTCOME.REFUSED, message: message, fields: fields }; }
      if (status === CORRECTABLE_STATUS) { return { outcome: OUTCOME.REFUSED, message: FALLBACK_400_TEXT, fields: [] }; }
      return { outcome: OUTCOME.AMBIGUOUS };
    });
  }

  /** A server field name as the target it paints; location's alias included. */
  function targetFor(name) {
    var key = name === 'ship_address' ? 'location' : name;
    for (var i = 0; i < ERROR_TARGETS.length; i++) {
      if (ERROR_TARGETS[i].key === key) { return ERROR_TARGETS[i]; }
    }
    return null;
  }

  /** " Check: Email address, Phone number." Unmapped names appear as sent. */
  function namedFields(fields) {
    var names = [];
    for (var i = 0; i < (fields || []).length; i++) {
      var t = targetFor(fields[i]);
      var name = t ? t.label : fields[i];
      if (!inClosedSet(names, name)) { names.push(name); }
    }
    return names.length ? ' Check: ' + names.join(', ') + '.' : '';
  }

  function serverFieldErrors(fields) {
    var errors = {};
    for (var i = 0; i < fields.length; i++) {
      var t = targetFor(fields[i]);
      if (t) { errors[t.key] = MESSAGES.server_field; }
    }
    var firstInvalid = null;
    for (var j = 0; j < ERROR_TARGETS.length && firstInvalid === null; j++) {
      if (errors[ERROR_TARGETS[j].key]) { firstInvalid = ERROR_TARGETS[j].key; }
    }
    return { errors: errors, firstInvalid: firstInvalid };
  }

  function byId(doc, id) { return doc.getElementById(id); }

  function readValues(doc, form) {
    var out = {};
    for (var i = 0; i < FIELDS.length; i++) {
      var el = byId(doc, FIELDS[i].id);
      out[FIELDS[i].key] = el ? el.value : '';
    }
    out.interests = [];
    for (var j = 0; j < INTERESTS.length; j++) {
      var box = byId(doc, INTEREST_ID_PREFIX + INTERESTS[j][1]);
      if (box && box.checked) { out.interests.push(INTERESTS[j][1]); }
    }
    var other = byId(doc, OTHER_TEXT_TARGET.id);
    out[OTHER_TEXT_KEY] = other ? other.value : '';
    var trap = byId(doc, HONEYPOT_ID);
    out[HONEYPOT_FIELD] = trap ? trap.value : '';
    var token = (form && typeof form.querySelector === 'function')
      ? form.querySelector('input[name="' + TOKEN_FIELD + '"]') : null;
    out[TOKEN_FIELD] = token && token.value ? token.value : '';
    return out;
  }

  function paintErrors(doc, errors, firstInvalid) {
    var focusEl = null;
    for (var i = 0; i < ERROR_TARGETS.length; i++) {
      var t = ERROR_TARGETS[i];
      var control = t.id ? byId(doc, t.id) : null;
      var node = byId(doc, t.errorId);
      var msg = errors[t.key] || '';
      if (node) { node.textContent = msg; node.hidden = !msg; }
      if (control) {
        if (msg) { control.setAttribute('aria-invalid', 'true'); }
        else { control.removeAttribute('aria-invalid'); }
      }
      if (t.key === firstInvalid) { focusEl = control || byId(doc, t.focusId); }
    }
    if (focusEl && typeof focusEl.focus === 'function') { focusEl.focus(); }
  }

  function setStatus(doc, text) {
    var s = byId(doc, 'applyStatus');
    if (s) { s.textContent = text; }
  }

  /** The one role="alert" region; every error the applicant sees lands here. */
  function setAlert(doc, text) {
    var a = byId(doc, 'applyError');
    if (a) { a.textContent = text; }
  }

  /**
   * #applyStatus lives outside #applyFormView, so swapping panels does not
   * remove the polite live region from the accessibility tree. Ordering alone
   * is insufficient: the markup placement is what preserves the region.
   */
  function showSuccess(doc) {
    var view = byId(doc, 'applyFormView');
    var ok = byId(doc, 'applySuccess');
    setAlert(doc, '');
    setStatus(doc, 'Application received.');
    if (view) { view.style.display = 'none'; }
    if (ok) { ok.style.display = 'block'; }
    var heading = byId(doc, 'applySuccessHeading');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      if (typeof heading.focus === 'function') { heading.focus(); }
    }
  }

  /**
   * @param doc  document, or a test stub exposing getElementById
   * @param opts { fetch, turnstile } — injectable transport and widget API.
   *             Production passes nothing; the module reads the global fetch
   *             and window.turnstile (loaded async by the section).
   *
   * Idempotent: a second call on the same document returns the existing
   * handle and attaches no second listener.
   *
   * If no transport is available (fetch missing, or explicitly null), the
   * handler does NOT preventDefault and does NOT set noValidate, so the
   * browser performs the form's native POST with native constraint UI. That
   * is the asset-failure path: a degraded page still submits over POST and
   * never falls back to a GET that would put identity in a query string.
   */
  function mount(doc, opts) {
    if (!doc || typeof doc.getElementById !== 'function') { return null; }
    var form = byId(doc, 'applyForm');
    if (!form || typeof form.addEventListener !== 'function') { return null; }
    if (form.__qccWholesaleApplyMounted) { return form.__qccWholesaleApplyMounted; }

    var options = opts || {};
    var has = function (k) { return Object.prototype.hasOwnProperty.call(options, k); };
    var send = has('fetch') ? options.fetch : (typeof fetch === 'function' ? fetch : null);
    var inFlight = false;

    function widgetApi() {
      if (has('turnstile')) { return options.turnstile; }
      return (typeof window !== 'undefined' && window.turnstile) ? window.turnstile : null;
    }

    /** Tokens are single-use: every non-success needs a fresh one. */
    function resetWidget() {
      var api = widgetApi();
      var box = byId(doc, TURNSTILE_ID);
      if (!api || typeof api.reset !== 'function' || !box) { return; }
      try { api.reset(box); } catch (e) { /* a missing widget must not block the error copy */ }
    }

    /* "Something else" reveals its text field. Without script the field is
       simply visible; with script it is hidden and disabled until chosen, so
       even the native POST never carries text without `other`. */
    var otherBox = byId(doc, INTEREST_ID_PREFIX + OTHER_INTEREST);
    var otherWrap = byId(doc, OTHER_TEXT_TARGET.wrapId);
    var otherInput = byId(doc, OTHER_TEXT_TARGET.id);
    function syncOther() {
      var on = !!(otherBox && otherBox.checked);
      if (otherWrap) { otherWrap.hidden = !on; }
      if (otherInput) { otherInput.disabled = !on; }
    }
    if (otherBox && typeof otherBox.addEventListener === 'function') {
      otherBox.addEventListener('change', syncOther);
      syncOther();
    }

    function settle(btn, result) {
      resetWidget();
      setStatus(doc, '');
      if (result.outcome === OUTCOME.REFUSED) {
        setAlert(doc, result.message + namedFields(result.fields));
        if (result.fields && result.fields.length) {
          var painted = serverFieldErrors(result.fields);
          paintErrors(doc, painted.errors, painted.firstInvalid);
        }
        inFlight = false;
        if (btn) { btn.disabled = false; }
        return;
      }
      setAlert(doc, AMBIGUOUS_TEXT);
    }

    function submit(evt) {
      if (!send) { return; }   // native POST proceeds; do not preventDefault
      if (evt && typeof evt.preventDefault === 'function') { evt.preventDefault(); }
      if (inFlight) { return; }

      var values = readValues(doc, form);
      var result = validate(values);
      paintErrors(doc, result.errors, result.firstInvalid);
      if (!result.ok) {
        setStatus(doc, '');
        setAlert(doc, CHECK_FIELDS_TEXT);
        return;
      }

      inFlight = true;
      setAlert(doc, '');
      setStatus(doc, 'Submitting your application…');
      var btn = byId(doc, 'applySubmit');
      if (btn) { btn.disabled = true; }

      var pending;
      try {
        pending = send(APPLY_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload(values))
        });
      } catch (e) {
        settle(btn, { outcome: OUTCOME.AMBIGUOUS });
        return Promise.resolve();
      }

      return Promise.resolve(pending).then(interpretResponse).then(function (r) {
        if (r.outcome === OUTCOME.OK) {
          inFlight = false;
          showSuccess(doc);
          return;
        }
        settle(btn, r);
      }).catch(function () {
        settle(btn, { outcome: OUTCOME.AMBIGUOUS });
      });
    }

    form.addEventListener('submit', submit);
    if (send) { form.noValidate = true; }

    var handle = { submit: submit, isMounted: function () { return true; } };
    form.__qccWholesaleApplyMounted = handle;
    return handle;
  }

  function autoMount() {
    if (typeof document === 'undefined') { return; }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { mount(document); });
    } else {
      mount(document);
    }
  }
  return {
    APPLY_ENDPOINT: APPLY_ENDPOINT,
    LEGACY_APPLY_SUCCESS_URL: LEGACY_APPLY_SUCCESS_URL,
    WS_RANGES: WS_RANGES,
    WS_CLASS: WS_CLASS,
    trim: trim,
    canonicalSuccessUrl: canonicalSuccessUrl,
    isLegacySuccessUrl: isLegacySuccessUrl,
    OUTCOME: OUTCOME,
    MESSAGE_STATUSES: MESSAGE_STATUSES,
    CORRECTABLE_STATUS: CORRECTABLE_STATUS,
    SUPPORT_CONTACT: SUPPORT_CONTACT,
    FIELDS: FIELDS,
    INTERESTS: INTERESTS,
    OTHER_TEXT_MAX: OTHER_TEXT_MAX,
    HONEYPOT_FIELD: HONEYPOT_FIELD,
    TOKEN_FIELD: TOKEN_FIELD,
    BUSINESS_TYPES: BUSINESS_TYPES,
    ND_UNICODE_VERSION: ND_UNICODE_VERSION,
    ND_SOURCE_RUNTIME: ND_SOURCE_RUNTIME,
    ND_RANGES: ND_RANGES,
    digitCount: digitCount,
    isValidEmail: isValidEmail,
    isValidPhone: isValidPhone,
    interpretResponse: interpretResponse,
    validate: validate,
    buildPayload: buildPayload,
    mount: mount,
    autoMount: autoMount
  };
}));
